/**
 * Fitness card on the Rides page (CHARTS_PLAN §5): Fitness / Fatigue /
 * Form tiles, the CTL / ATL / TSB trend and a Weekly load tab.
 */
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { RideAttribution } from '../GarminAttribution.tsx';
import { useFitness } from '../../hooks/useFitness.ts';
import { useNoRides } from '../../hooks/useSetup.ts';
import { NoRidesCallout } from '../setup/NoRidesCallout.tsx';
import { useStoredChoice } from '../../hooks/useStoredChoice.ts';
import { useFitnessExpanded } from '../../hooks/useFitnessExpanded.ts';
import { LucideIcon } from '../icons/LucideIcon.tsx';
import { RANGE_DAYS, fitnessIsEmpty, formatSigned, type RangeDays } from '../../lib/chartData.ts';
import { formatDayLong } from '../../lib/chartFormat.ts';
import type { FitnessResponse } from '../../types/api.ts';
import {
  ChartCard,
  ChartMessage,
  ChartSkeleton,
  ChipGroup,
  InfoTip,
  RangeChips,
} from './ChartParts.tsx';
import { FitnessTrendChart } from './FitnessTrendChart.tsx';
import { WeeklyLoadChart } from './WeeklyLoadChart.tsx';

type Tab = 'trend' | 'weekly';
const TABS: readonly Tab[] = ['trend', 'weekly'];

export function FitnessCard() {
  const { t } = useTranslation();
  const [range, setRange] = useStoredChoice<RangeDays>('fitness_range', RANGE_DAYS, '90');
  const [tab, setTab] = useStoredChoice<Tab>('fitness_tab', TABS, 'trend');
  const [expanded, toggle] = useFitnessExpanded();
  const toggleButton = <ExpandToggle expanded={expanded} onToggle={toggle} />;
  if (!expanded) {
    return (
      <ChartCard title={t('charts.fitness.title')} actions={toggleButton}>
        <CollapsedLine days={Number(range)} onExpand={toggle} />
      </ChartCard>
    );
  }
  return (
    <ChartCard
      title={t('charts.fitness.title')}
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          <RangeChips value={range} onChange={setRange} />
          {toggleButton}
        </div>
      }
    >
      <ChipGroup
        label={t('charts.fitness.view')}
        value={tab}
        onChange={setTab}
        options={[
          { value: 'trend', label: t('charts.fitness.trendTab') },
          { value: 'weekly', label: t('charts.fitness.weeklyTab') },
        ]}
      />
      <FitnessBody days={Number(range)} tab={tab} />
    </ChartCard>
  );
}

function ExpandToggle({ expanded, onToggle }: Readonly<{ expanded: boolean; onToggle: () => void }>) {
  const { t } = useTranslation();
  const label = t(expanded ? 'dashboard.fitnessHide' : 'dashboard.fitnessShow');
  return (
    <button
      type="button"
      aria-expanded={expanded}
      aria-label={label}
      title={label}
      onClick={onToggle}
      className="h-7 w-7 rounded-full border border-(--color-border) text-(--color-text-secondary) hover:text-(--color-text-primary) flex items-center justify-center"
    >
      <LucideIcon name={expanded ? 'chevron-up' : 'chevron-down'} size={16} />
    </button>
  );
}

/** "Fitness 63 · Fatigue 75 · Form -11 Productive"; it opens the card. */
function CollapsedLine({ days, onExpand }: Readonly<{ days: number; onExpand: () => void }>) {
  const { t } = useTranslation();
  const { data } = useFitness(days);
  const today = data?.today;
  if (!today) return null;
  const formLabel = t('charts.form.' + today.form);
  const line = [
    `${t('charts.fitness.ctl')} ${Math.round(today.ctl)}`,
    `${t('charts.fitness.atl')} ${Math.round(today.atl)}`,
    `${t('charts.fitness.tsb')} ${formatSigned(today.tsb)} ${formLabel}`,
  ].join(' · ');
  return (
    <button type="button" onClick={onExpand} className="self-start text-left text-sm text-(--color-text-primary)">
      {line}
    </button>
  );
}

function FitnessBody({ days, tab }: Readonly<{ days: number; tab: Tab }>) {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError, refetch } = useFitness(days);
  const noRides = useNoRides();
  if (isLoading) return <ChartSkeleton />;
  if (isError || !data) {
    return (
      <ChartMessage role="alert">
        {t('charts.loadError')}{' '}
        <button type="button" onClick={() => refetch()} className="underline">
          {t('dashboard.retry')}
        </button>
      </ChartMessage>
    );
  }
  if (fitnessIsEmpty(data.points)) {
    return noRides ? <NoRidesCallout variant="inline" /> : <ChartMessage>{t('charts.fitness.empty')}</ChartMessage>;
  }
  return (
    <>
      <FitnessTiles today={data.today} />
      {tab === 'trend' ? (
        <FitnessTrendChart points={data.points} summary={trendSummary(data, t, i18n.language)} />
      ) : (
        <WeeklyLoadChart points={data.points} />
      )}
      <RideAttribution text={data.data_sources.join(', ')} />
    </>
  );
}

function trendSummary(data: FitnessResponse, t: TFunction, locale: string): string {
  const first = data.points[0];
  const last = data.points.at(-1);
  if (!first || !last) return '';
  return t('charts.fitness.summary', {
    from: formatDayLong(first.date, locale),
    to: formatDayLong(last.date, locale),
    ctlStart: Math.round(first.ctl),
    ctlEnd: Math.round(last.ctl),
    atl: Math.round(last.atl),
    tsb: formatSigned(last.tsb),
  });
}

function FitnessTiles({ today }: Readonly<{ today: FitnessResponse['today'] }>) {
  const { t } = useTranslation();
  if (!today) return null;
  return (
    <div className="grid grid-cols-3 gap-2">
      <Tile name={t('charts.fitness.ctl')} value={`${Math.round(today.ctl)}`} info={t('charts.fitness.ctlInfo')} />
      <Tile name={t('charts.fitness.atl')} value={`${Math.round(today.atl)}`} info={t('charts.fitness.atlInfo')} />
      <Tile
        name={t('charts.fitness.tsb')}
        value={formatSigned(today.tsb)}
        info={t('charts.fitness.tsbInfo')}
        note={t(`charts.form.${today.form}`)}
      />
    </div>
  );
}

function Tile({
  name,
  value,
  info,
  note,
}: Readonly<{ name: string; value: string; info: string; note?: string }>) {
  return (
    <div className="rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) p-2 text-center">
      <div className="flex flex-wrap items-center justify-center gap-1 text-[11px] text-(--color-text-secondary) uppercase tracking-wide">
        <span>{name}</span>
        <InfoTip label={name} text={info} />
      </div>
      <div className="text-lg font-semibold text-(--color-text-primary)">{value}</div>
      {note && <div className="text-xs text-(--color-text-secondary)">{note}</div>}
    </div>
  );
}
